import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { fetchWithAuth, postWithUsername } from "../services/fetchWrapper";
import { useAuth } from "../utils/AuthContext";
// import Layout from "./Layout";
import { Environment } from "../utils/Environment";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SuccessMessageComponent from "./SuccessMessageComponent";

const apiUrl = Environment.getApiUrl();

const UploadService: React.FC = () => {
  const { isLoggedIn, userName } = useAuth();
  // const [uploadResult, setUploadResult] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [form, setForm] = useState({
    layer_name: "",
    table_passed: "",
    client_id: "",
    client_name: "",
    job_id: "",
    base_dataset: "true",
  });
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [isLayerNameRequired, setIsLayerNameRequired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Fetch client data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const username = localStorage.getItem("userName");
        if (!username) {
          console.error("No username found in localStorage");
          setLoading(false);
          return;
        }
        const response = await postWithUsername(`${apiUrl}/get-client-data`, username);
        const data = await response.json();
        setForm((prevForm) => ({
          ...prevForm,
          client_id: data.client_id,
          client_name: data.client_name,
          job_id: localStorage.getItem("job_id") || "", // Always use latest job_id from localStorage
        }));
      } catch (error) {
        console.error("Failed to fetch client data:", error);
        alert("Failed to fetch client data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    setSelectedFiles(files);
    setShowSuccess(false);
    setUploadError(null);

    if (files && files.length > 0) {
      const fileName = files[0].name.toLowerCase();
      if (fileName.endsWith(".gdb.zip")) {
        setIsLayerNameRequired(true);
      } else {
        setIsLayerNameRequired(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFiles || selectedFiles.length === 0) {
      setShowSuccess(false);
      setUploadError("Please select at least one file before uploading.");
      return;
    }
    setShowSuccess(false);
    setUploadError(null);
    setUploading(true);
    const formData = new FormData();
    Array.from(selectedFiles).forEach((file) => {
      formData.append("files", file);
    });
    if (userName) {
      formData.append("username", userName);
    } else {
      setUploadError("Username is missing. Please log in again.");
      setUploading(false);
      return;
    }
    formData.append("layer_name", form.layer_name);
    formData.append("table_passed", form.table_passed.toLowerCase());
    formData.append("client_id", form.client_id);
    formData.append("client_name", form.client_name);
    formData.append("job_id", localStorage.getItem("job_id") || "");
    formData.append("base_dataset", form.base_dataset);

    try {
      const response = await fetchWithAuth(`${apiUrl}/load-base-gis-data`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        // Try to extract backend error message
        let errorMsg = "Upload failed";
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMsg = errorData.detail;
          }
        } catch {
          // fallback to default error message
        }
        setShowSuccess(false);
        setUploadError(errorMsg);
        return;
      }

      const data = await response.json();
      // setUploadResult(data);
      setShowSuccess(true);
      setUploadError(null);
      localStorage.setItem("baseDatasetName", data.table_name);
    } catch (err) {
      setShowSuccess(false);
      setUploadError("Upload failed. Please try again.");
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          pt: 6,
        }}
      >
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          pt: 6,
        }}
      >
        <Typography variant="h6" color="error">
          You must be logged in to access this page.
        </Typography>
      </Box>
    );
  }

  return (
    // <Layout isLoggedIn={isLoggedIn} onLogout={logout} userName={userName}>
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        pt: 6,
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, textTransform: "uppercase" }}>
        Upload Base GIS Data Set
      </Typography>
      <svg width="292" height="6" viewBox="0 0 292 6" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 16 }}>
        <path d="M0 3H292" stroke="#8AD019" strokeWidth="6" />
      </svg>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          borderRadius: 1,
          p: 2,
          mb: 1, // Small margin below note
          gap: 1,
          maxWidth: 400,
          width: "100%",
        }}
      >
        {/* <InfoIcon color="primary" sx={{ mt: "2px" }} /> */}
        <Typography sx={{ fontSize: 16 }}>
          Upload your zipped (e.g., .zip) base GIS dataset below. Please provide a name for your dataset, and if it is a geodatabase, please provide the exact name of the layer you are uploading.
        </Typography>
      </Box>
      {showSuccess && (
        <SuccessMessageComponent message="Data Uploaded Successfully!" />
      )}
      {uploadError && (
        <Typography variant="body2" color="error" sx={{ mb: 2, textAlign: "center" }}>
          {uploadError}
        </Typography>
      )}
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 20,
          maxWidth: 400,
          width: "100%",
        }}
      >
        {/* select file button style/ logic */}
        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setShowSuccess(false);
            setUploadError(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
              fileInputRef.current.click();
            }
          }}
          sx={{
            textTransform: "none",
            width: "fit-content",
            margin: "0 auto",
            backgroundColor: "#0373B5"
          }}
        >
          Select Files
        </Button>
        {selectedFiles && selectedFiles.length > 0 && (
          <Typography variant="body2" sx={{ mt: 1, color: "textSecondary" }}>
            {Array.from(selectedFiles).map(f => f.name).join(", ")}
          </Typography>
        )}
        {/* Begin tooltip styling logic */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <input
            name="table_passed"
            placeholder="Dataset Name"
            value={form.table_passed}
            onChange={handleInputChange}
            required
            style={{
              backgroundColor: "#F6FFE8",
              width: "320px",
              maxWidth: "100%",
              height: "36px",
              padding: "6px 10px",
              boxSizing: "border-box",
            }}
          />
          <Tooltip
            title='Add the name of the dataset you are uploading (e.g., "Water Mains", "Active Pipes", etc.)'
            arrow
          >
            <IconButton size="small" aria-label="Dataset name help" sx={{ color: "#5F5F5F" }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {isLayerNameRequired && (
          <>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <input
                name="layer_name"
                placeholder="Layer Name"
                value={form.layer_name}
                onChange={handleInputChange}
                required
                style={{
                  backgroundColor: "#F6FFE8",
                  width: "320px",
                  maxWidth: "100%",
                  height: "36px",
                  padding: "6px 10px",
                  boxSizing: "border-box",
                }}
              />
              <Tooltip
                title="Enter the exact layer name from your geodatabase (.gdb.zip)."
                arrow
              >
                <IconButton size="small" aria-label="Layer name help" sx={{ color: "#5F5F5F" }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Layer Name is required for geodatabases (.gdb.zip files).
            </Typography>
          </>
        )}
        {/* Hidden Fields */}
        <input type="hidden" name="client_id" value={form.client_id} />
        <input type="hidden" name="client_name" value={form.client_name} />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={<CloudUploadIcon />}
          sx={{
            mt: 2,
            textTransform: "none",
            width: "fit-content",
            minWidth: "132px",
            margin: "0 auto",
            backgroundColor: "#0373B5"
          }}
          disabled={uploading}
        >
          {uploading ? "Uploading..." : "Upload"}
        </Button>
      </form>
      {
        uploading && (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              Uploading...
            </Typography>
            <CloudUploadIcon />
          </Box>
        )
      }
      {/* {uploadResult && (
          <Box sx={{ mt: 2, p: 2, border: "1px solid #ccc", borderRadius: 2 }}>
            <Typography variant="h6">Upload Result</Typography>
            <pre style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(uploadResult, null, 2)}</pre>
          </Box>
        )} */}
    </Box >
    // </Layout>
  );
};

export default UploadService;